import React, { useCallback } from "react";

import { mdiPlus } from "@mdi/js";
import Icon from "@mdi/react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import AdbreakRuleIcon from "./icons/AdbreakRuleIcon";
import RandomRuleIcon from "./icons/RandomRuleIcon";
import SequentialRuleIcon from "./icons/SequentialRuleIcon";
import SpecificRuleIcon from "./icons/SpecificRuleIcon";
import TimeslotRuleListContainer from "./TimeslotRuleListContainer";
import { TimeslotRuleMode } from "../../../api/FastAPI.types";
import Block from "../../block/Block";
import Button, { BUTTON_SIZES, BUTTON_TYPES } from "../../button/Button";

import type { JSX } from "react";

import type { TimeslotFormValues } from "./TimeslotEdit";
import type { TimeslotRule } from "../../../api/FastAPI.types";

const addNewRuleByMode = (
  rank: number,
): Record<TimeslotRuleMode, TimeslotRule> => ({
  [TimeslotRuleMode.Random]: {
    mode: TimeslotRuleMode.Random,
    uuid: uuidv4(),
    rank,
    elements: 1,
    fast_channel_playlist_uuid: "",
    adbreak_uuid: null,
  },
  [TimeslotRuleMode.Sequential]: {
    mode: TimeslotRuleMode.Sequential,
    uuid: uuidv4(),
    rank,
    elements: 1,
    fast_channel_playlist_uuid: "",
    sequential_delta: 0,
    sequential_start_index: 0,
    adbreak_uuid: null,
  },
  [TimeslotRuleMode.Specific]: {
    mode: TimeslotRuleMode.Specific,
    uuid: uuidv4(),
    rank,
    elements: 1,
    media_uuid: "",
  },
  [TimeslotRuleMode.Adbreak]: {
    mode: TimeslotRuleMode.Adbreak,
    uuid: uuidv4(),
    rank,
    elements: 1,
    adbreak_uuid: "",
  },
});

interface TimeslotStructureBlockProps {
  isLoading: boolean;
}

export default function TimeslotStructureBlock({
  isLoading,
}: TimeslotStructureBlockProps): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");
  const { watch, setValue } = useFormContext<TimeslotFormValues>();

  const rulesById = watch("rulesById");
  const ruleList = Object.values(rulesById);

  const handleAddRule = useCallback(
    (ruleMode: TimeslotRuleMode): void => {
      const newRule = addNewRuleByMode(ruleList.length)[ruleMode];

      setValue("rulesById", {
        ...rulesById,
        [newRule.uuid]: newRule,
      });
    },
    [ruleList, rulesById, setValue],
  );

  const dropdownItemList = [
    {
      label: t("timeslotRuleMode.random"),
      icon: <RandomRuleIcon />,
      onClick: () => handleAddRule(TimeslotRuleMode.Random),
    },
    {
      label: t("timeslotRuleMode.sequential"),
      icon: <SequentialRuleIcon />,
      onClick: () => handleAddRule(TimeslotRuleMode.Sequential),
    },
    {
      label: t("timeslotRuleMode.specific"),
      icon: <SpecificRuleIcon />,
      onClick: () => handleAddRule(TimeslotRuleMode.Specific),
    },
    {
      label: t("timeslotRuleMode.adbreak"),
      icon: <AdbreakRuleIcon />,
      onClick: () => handleAddRule(TimeslotRuleMode.Adbreak),
    },
  ];

  const addButton = (
    <Button
      dropdownItemList={dropdownItemList}
      size={BUTTON_SIZES.MD}
      type={BUTTON_TYPES.BUTTON}
    >
      <div className="flex items-center justify-center gap-[0.46875rem]">
        <Icon path={mdiPlus} size={0.9375} /> {t("addTimeslotRule")}
      </div>
    </Button>
  );

  return (
    <Block
      isFullSize
      headerComponent={addButton}
      title={t("timeslotStructureTitle")}
    >
      <TimeslotRuleListContainer isLoading={isLoading} />
    </Block>
  );
}
